/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { SmartCardProvider } from '@atlaskit/link-provider/smart-card-provider';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { AnimatedWrapper } from '@atlassian/ai-common';

import { useAIAnswerContext } from '../../../../ai-answer-dialog/ai-answer-context';
import { onJiraNlToJqlCardLoaded } from '../../../../ai-answer-dialog/on-jira-nl-to-jql-card-loaded';
import { onJiraViewAllButtonClicked } from '../../../../ai-answer-dialog/on-jira-view-all-button-clicked';
import { SearchAIAnalyticsContext } from '../../../../ai-answer-dialog/search-ai-analytics-context';
import { SearchAIAnalyticsContextLocation } from '../../../../ai-answer-dialog/search-ai-analytics-context-location';
import type { NonGenerativeAIAnswerResultShownProps } from '../../../../ai-answer-dialog/types';
import { SainAction } from '../../../../common/controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../../common/controller/analytics/use-analytics';
import { logger } from '../../../../common/utils/logger';
import { SAINFeatureControl } from '../../../../common/utils/sain-feature-control';
import { type AiBrand, AnswerCardType } from '../../../../constants';
import { sha256Hash } from '../../../../sha256-hash';
import { AIDisclaimerAndFeedback } from '../../../common/ui/ai-disclaimer-and-feedback';
import { SmartAnswerCardBodyGenerating } from '../../../common/ui/smart-answer-card-body-generating';
import { useIssueSearchQuery } from '../../api/useIssueSearchQuery/useIssueSearchQuery';
import { useNaturalLanguageToJqlQuery } from '../../api/useNaturalLanguageToJqlQuery/useNaturalLanguageToJqlQuery';
import { buildKeywordJql } from './buildKeywordJql';
import { ErrorCard } from './ErrorCard';
import { IssueTable } from './IssueTable';
import { messages } from './messages';
import { ViewAllButton } from './ViewAllButton';

const styles = cssMap({
	container: {
		borderRadius: token('radius.small'),
		border: `${token('border.width')} solid ${token('color.border')}`,
	},
	tableContainer: {
		position: 'relative',
		borderRadius: token('radius.large'),
		marginLeft: token('space.200'),
		marginRight: token('space.200'),
		marginBottom: token('space.100'),
	},
	sidePadding: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	headerContainer: {
		paddingTop: token('space.150'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.100'),
	},
	footer: {
		paddingInline: token('space.200'),
		paddingBlockEnd: token('space.150'),
	},
	viewAllContainer: {
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
		textAlign: 'left',
	},
});

const buildJiraLink = ({ issueUrl, jql }: { issueUrl: string; jql: string }): string => {
	const hostname = new URL(issueUrl).hostname;
	const jiraLink = new URL(`https://${hostname}/issues/`);
	jiraLink.searchParams.append('jql', jql);
	return jiraLink.toString();
};

export const JiraNlToJqlCard: import('react').MemoExoticComponent<
	(props: {
		query: string;
		title?: string | null;
		cloudId: string;
		aiBrand: AiBrand;
		searchSessionId: string;
		workspaceId: string;
		product: string;
		onFMPReached: () => void;
		/**
		 * When `true`, bypass the agentic NL→JQL flow and use a deterministic
		 * keyword JQL of the form `textfields ~ "<query>*"`.
		 */
		bypassWithKeywordJql?: boolean;
	}) => JSX.Element
> = memo(
	(props: {
		query: string;
		title?: string | null;
		cloudId: string;
		aiBrand: AiBrand;
		searchSessionId: string;
		workspaceId: string;
		product: string;
		onFMPReached: () => void;
		bypassWithKeywordJql?: boolean;
	}) => {
		const renderStartTime = useRef(performance.now());
		const analyticsFiredRef = useRef(false);
		const nlToJqlEndTimeRef = useRef<number | undefined>(undefined);
		const issueSearchStartTimeRef = useRef<number | undefined>(undefined);
		const {
			query,
			title,
			cloudId,
			aiBrand,
			searchSessionId,
			workspaceId,
			product,
			onFMPReached,
			bypassWithKeywordJql = false,
		} = props;
		const [issueUrl, setIssueUrl] = useState<string | null>(null);
		const { formatMessage } = useIntl();
		const [fireAnalyticsEvent] = useAnalytics();
		const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
		const { trackAIInteractionInit } = useAIEventsInstrumentation();
		const { commonAttributes, setData, callbacks } = useAIAnswerContext();
		const onSainJqlResolved = callbacks?.onSainJqlResolved;

		// When bypassing the agentic NL→JQL flow (JIRA_NLQ_KEYWORD intent), compute
		// the deterministic keyword JQL synchronously and skip the network call.
		const keywordJql = useMemo(
			() => (bypassWithKeywordJql ? buildKeywordJql(query) : null),
			[bypassWithKeywordJql, query],
		);

		const nlResult = useNaturalLanguageToJqlQuery({
			cloudId,
			query,
			workspaceId,
			product,
			enabled: !bypassWithKeywordJql,
		});

		const jqlLoading = bypassWithKeywordJql ? false : nlResult.loading;
		// In bypass mode, only the empty-query case is treated as an error here;
		// downstream `issues?.length === 0` still drives the empty-state UI.
		const jqlError = bypassWithKeywordJql ? keywordJql === null : nlResult.error;
		const jql = bypassWithKeywordJql ? (keywordJql ?? undefined) : nlResult.jql;

		useEffect(() => {
			onSainJqlResolved?.(jql ?? null);
		}, [jql, onSainJqlResolved]);

		const {
			issues,
			totalIssuesCount,
			loading: issuesLoading,
			error: issuesError,
			extensions: issuesExtensions,
		} = useIssueSearchQuery({ cloudId, jql, workspaceId, product });

		const analyticsAttributes = useMemo<NonGenerativeAIAnswerResultShownProps>(
			() => ({
				searchSessionId,
				queryHash: sha256Hash(query),
				brand: aiBrand,
				source: 'jira_nl_2_jql_card',
				query: query,
				answerString: jql || '',
				answerCardType: AnswerCardType.JIRA_NATURAL_LANGUAGE_QUERY,
				queryLength: query.length,
				wordCount: (query.match(/\w+/g) || []).length,
				queryIntent: commonAttributes.queryIntent,
				jiraNlToJqlCardVariant: 'issue-table' as const,
			}),
			[searchSessionId, query, aiBrand, jql, commonAttributes.queryIntent],
		);

		const analyticsAttributesWithoutUGC = useMemo<
			Omit<NonGenerativeAIAnswerResultShownProps, 'query' | 'answerString'>
		>(
			() => ({
				searchSessionId,
				queryHash: sha256Hash(query),
				brand: aiBrand,
				source: 'jira_nl_2_jql_card',
				answerCardType: AnswerCardType.JIRA_NATURAL_LANGUAGE_QUERY,
			}),
			[searchSessionId, query, aiBrand],
		);

		const fireJiraNlToJqlCardLoaded = useCallback(
			({
				success,
				endState,
			}: {
				success: boolean;
				endState?: 'answered' | 'no-issues' | 'jql-error' | 'issues-error';
			}) => {
				const apiLoadedTime = performance.now();
				const totalDuration = apiLoadedTime - renderStartTime.current;
				const nlToJqlLoadTime =
					nlToJqlEndTimeRef.current !== undefined
						? nlToJqlEndTimeRef.current - renderStartTime.current
						: undefined;
				const issueSearchLoadTime =
					issueSearchStartTimeRef.current !== undefined
						? apiLoadedTime - issueSearchStartTimeRef.current
						: undefined;

				const phaseTimingAttributes = {
					nlToJqlLoadTime,
					issueSearchLoadTime,
					...(nlToJqlLoadTime !== undefined || issueSearchLoadTime !== undefined
						? { phaseTimingSource: 'measured' as const }
						: {}),
				};

				fireAnalyticsEvent(
					onJiraNlToJqlCardLoaded({
						...analyticsAttributes,
						success,
						loadTime: totalDuration,
						endState,
						jiraNlToJqlCardVariant: 'issue-table',
						...phaseTimingAttributes,
					}),
				);
				SAINFeatureControl.triggerNonPrimaryClickTrackingExposure();
			},
			[fireAnalyticsEvent, analyticsAttributes],
		);

		// using the first issue's webUrl to build a jira url for the view all 'x' items button
		useEffect(() => {
			if (!issuesLoading && !issuesError && issues?.length && issues[0]) {
				try {
					const firstIssueUrl = issues[0].node.webUrl;
					setIssueUrl(buildJiraLink({ issueUrl: firstIssueUrl || '', jql: jql || '' }));
				} catch (error) {
					logger.error({
						feature: 'jira-nlq-to-smartlink',
						element: 'smart-card',
						operation: 'parse',
						category: 'parse',
						error,
						functionName: 'JiraNlToJqlCard',
					});
				}
			}
		}, [issuesLoading, issuesError, issues, jql]);

		useEffect(() => {
			if (jqlLoading) {
				return;
			}
			if (jqlError && nlToJqlEndTimeRef.current === undefined) {
				nlToJqlEndTimeRef.current = performance.now();
				return;
			}
			if (jql && nlToJqlEndTimeRef.current === undefined) {
				const now = performance.now();
				nlToJqlEndTimeRef.current = now;
				issueSearchStartTimeRef.current = now;
			}
		}, [jqlLoading, jqlError, jql]);

		useEffect(
			() => {
				trackAIInteractionInit();
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[],
		);

		// Set debug data for online debugger when API response is available
		// Separate from analytics effect to avoid infinite loop with commonAttributes
		useEffect(() => {
			if (!issuesLoading && issuesExtensions?.gateway && fg('kd_sain_online_debugger')) {
				setData({
					errorState: null,
					format: null,
					uniqueSources: [],
					disclaimer: null,
					extraAPIAnalyticsAttributes: {
						traceId: issuesExtensions.gateway.trace_id ?? null,
						requestId: issuesExtensions.gateway.request_id ?? null,
					},
				});
			}
		}, [issuesLoading, issuesExtensions?.gateway, setData]);

		// fire Analytics Events on final state changes
		useEffect(() => {
			if (analyticsFiredRef.current) {
				return;
			}

			if (
				!jqlLoading &&
				!jqlError &&
				!issuesLoading &&
				!issuesError &&
				issues?.length &&
				issues[0]
			) {
				/*
				 * * Success block
				 */
				analyticsFiredRef.current = true;
				onFMPReached();
				fireJiraNlToJqlCardLoaded({ success: true, endState: 'answered' });
				if (fg('kd_fix_nl2jql_mau_instrumentation')) {
					fireSAINUsageEvent({
						commonAttributes: {
							...commonAttributes,
							extraAttributes: {
								...commonAttributes?.extraAttributes,
								requestId: issuesExtensions?.gateway?.request_id ?? null,
							},
						},
					});
				} else {
					fireSAINUsageEvent({ commonAttributes });
				}
			} else if (!jqlLoading && jqlError) {
				/*
				 * * JQL error block
				 */
				analyticsFiredRef.current = true;
				fireJiraNlToJqlCardLoaded({ success: false, endState: 'jql-error' });
			} else if (!issuesLoading && issuesError) {
				/*
				 * * Issues error block
				 */
				analyticsFiredRef.current = true;
				fireJiraNlToJqlCardLoaded({ success: false, endState: 'issues-error' });
			} else if (!jqlLoading && !issuesLoading && issues?.length === 0) {
				/*
				 * * No issues block,
				 * * while this is not an error, we're still marking it as a failure cause the user sees an error card
				 */
				analyticsFiredRef.current = true;
				fireJiraNlToJqlCardLoaded({ success: false, endState: 'no-issues' });
			}
		}, [
			jqlLoading,
			issuesLoading,
			issuesError,
			jqlError,
			issues,
			onFMPReached,
			fireJiraNlToJqlCardLoaded,
			fireAnalyticsEvent,
			fireSAINUsageEvent,
			commonAttributes,
			issuesExtensions?.gateway?.request_id,
		]);

		const fireEngagementEvent = useCallback(
			(sainAction: SainAction) => {
				fireSAINUsageEvent({
					metCriteria: sainAction,
					commonAttributes: {
						...commonAttributes,
						extraAttributes: {
							...commonAttributes?.extraAttributes,
							requestId: issuesExtensions?.gateway?.request_id ?? null,
						},
					},
				});
			},
			[fireSAINUsageEvent, commonAttributes, issuesExtensions?.gateway?.request_id],
		);

		const onViewAllButtonClicked = useCallback(() => {
			fireAnalyticsEvent(
				onJiraViewAllButtonClicked({
					...analyticsAttributes,
					totalIssuesCount: totalIssuesCount || 0,
				}),
			);
			if (fg('kd_fix_nl2jql_mau_instrumentation')) {
				fireEngagementEvent(SainAction.JIRA_NL_TO_JQL_VIEW_ALL);
			}
		}, [fireAnalyticsEvent, fireEngagementEvent, analyticsAttributes, totalIssuesCount]);

		if (jqlLoading || issuesLoading) {
			return (
				<AnimatedWrapper>
					<SmartAnswerCardBodyGenerating heightOverride="jiraNlToJqlCard" />
				</AnimatedWrapper>
			);
		}

		if (jqlError || issuesError || issues?.length === 0) {
			return <ErrorCard query={query} />;
		}

		return (
			<Box xcss={styles.container} testId="jira-nl-to-jql-card-wrapper">
				<Box xcss={cx(styles.headerContainer, styles.sidePadding)}>
					<Heading size="xsmall">{title || formatMessage(messages.cardTitle)}</Heading>
				</Box>

				<Box xcss={styles.tableContainer}>
					<SearchAIAnalyticsContext
						data={{
							attributes: {
								...analyticsAttributesWithoutUGC,
								location: SearchAIAnalyticsContextLocation.SAIN_JIRA_NL_TO_JQL_CARD,
							},
							location: SearchAIAnalyticsContextLocation.SAIN_JIRA_NL_TO_JQL_CARD,
						}}
					>
						<SmartCardProvider>
							<IssueTable
								issues={issues || []}
								analyticsAttributes={analyticsAttributes}
								fireEngagementEvent={fireEngagementEvent}
							/>
						</SmartCardProvider>
					</SearchAIAnalyticsContext>
				</Box>

				{totalIssuesCount && totalIssuesCount > 0 && issueUrl ? (
					<Box xcss={cx(styles.viewAllContainer, styles.sidePadding)}>
						<ViewAllButton
							totalIssuesCount={totalIssuesCount}
							url={issueUrl}
							callback={onViewAllButtonClicked}
						/>
					</Box>
				) : null}

				<Box xcss={styles.footer}>
					<AIDisclaimerAndFeedback showFeedbackButtons={true} />
				</Box>
			</Box>
		);
	},
);
